import React from "react";
import { useCurrentFrame } from "remotion";

export type OPZuraPose =
  | "idle" // Neutral listening/blinking
  | "talking" // Audio reactive dot-matrix soundwave
  | "thinking" // Glancing up, hand tapping chassis
  | "pointing" // Arm extended with mechanical pointer
  | "typing" // Coding on terminal with mini mechanical keys
  | "coffee"; // Relaxed holding miniature ceramic {Z} espresso cup

/* ========================================================================= */
/* OFFICIAL MASCOT: OP-ZURA (TEENAGE ENGINEERING / BRAUN / PLAYDATE HARDWARE)*/
/* Clean bone-white milled resin, monochrome dot-matrix LCD, tactile knobs    */
/* ========================================================================= */
export const TeenageEngineeringBot: React.FC<{
  size?: number;
  pose?: OPZuraPose;
  floating?: boolean;
}> = ({ size = 320, pose = "idle", floating = true }) => {
  const frame = useCurrentFrame();

  // Subtle natural floating
  const floatY = floating ? Math.sin(frame / 20) * 6 : 0;
  const floatRotate = floating ? Math.sin(frame / 30) * 1.8 : 0;

  // Dot matrix blinking (pixel cutoff, not smooth gradient)
  const isBlink = frame % 80 > 74 && pose !== "coffee";

  // Audio equalizer oscillation for talking pose
  const eqWave = Math.sin(frame * 0.5);

  // Typing hand jitter
  const typeTapL = Math.sin(frame * 0.8) * 3;
  const typeTapR = Math.cos(frame * 0.8) * 3;

  return (
    <div
      style={{
        width: size,
        height: size * 1.05,
        position: "relative",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        transform: `translateY(${floatY}px) rotate(${floatRotate}deg)`,
        transformOrigin: "center bottom",
      }}
    >
      {/* CHASSIS: MILLED MATTE BONE RESIN (Clean Flush Top) */}
      <div
        style={{
          width: size * 0.78,
          height: size * 0.74,
          backgroundColor: "#E8E6DF", // Warm bone/ceramic gray
          borderRadius: 36,
          border: "3.5px solid #1C1917",
          boxShadow: "6px 8px 0px #1C1917",
          position: "relative",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          padding: 16,
          boxSizing: "border-box",
        }}
      >
        {/* Stamped Model Identifier */}
        <div
          style={{
            width: "100%",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "0 4px 8px",
          }}
        >
          <span
            style={{
              fontFamily: "'Space Grotesk', monospace",
              fontSize: 11,
              fontWeight: 800,
              color: "#57534E",
              letterSpacing: 1.5,
            }}
          >
            OP-ZURA // TE-01
          </span>
          {/* Laser-cut Speaker Perforation Grid */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(4, 3px)",
              gap: 3,
            }}
          >
            {[...Array(8)].map((_, i) => (
              <div
                key={i}
                style={{
                  width: 3,
                  height: 3,
                  borderRadius: "50%",
                  backgroundColor: "#78716C",
                }}
              />
            ))}
          </div>
        </div>

        {/* 3. E-INK / REFLECTIVE MONOCHROME LCD SCREEN */}
        <div
          style={{
            width: "100%",
            height: "72%",
            backgroundColor: "#B8C4B4", // Authentic GameBoy/Playdate reflective green-gray LCD
            borderRadius: 20,
            border: "2.5px solid #1C1917",
            boxShadow: "inset 2px 2px 6px rgba(0,0,0,0.25)",
            position: "relative",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            overflow: "hidden",
          }}
        >
          {/* LCD Segment Matrix Pattern */}
          <div
            style={{
              position: "absolute",
              inset: 0,
              backgroundImage:
                "linear-gradient(rgba(0,0,0,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(0,0,0,0.06) 1px, transparent 1px)",
              backgroundSize: "6px 6px",
              pointerEvents: "none",
            }}
          />

          {/* POSE SPECIFIC LCD SCREEN VISUALS */}
          {pose === "idle" && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 36,
                zIndex: 2,
              }}
            >
              <div
                style={{
                  width: 30,
                  height: isBlink ? 4 : 38,
                  backgroundColor: "#1C1917",
                  borderRadius: isBlink ? 2 : 8,
                }}
              />
              <div
                style={{
                  width: 30,
                  height: isBlink ? 4 : 38,
                  backgroundColor: "#1C1917",
                  borderRadius: isBlink ? 2 : 8,
                }}
              />
            </div>
          )}

          {pose === "talking" && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 20,
                zIndex: 2,
              }}
            >
              <div
                style={{
                  width: 28,
                  height: 36,
                  backgroundColor: "#1C1917",
                  borderRadius: 6,
                }}
              />
              {/* LCD Audio Bars */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 3,
                  height: 28,
                }}
              >
                <div
                  style={{
                    width: 4,
                    height: 10 + eqWave * 6,
                    backgroundColor: "#1C1917",
                  }}
                />
                <div
                  style={{
                    width: 4,
                    height: 22 + eqWave * 8,
                    backgroundColor: "#1C1917",
                  }}
                />
                <div
                  style={{
                    width: 4,
                    height: 14 + eqWave * 6,
                    backgroundColor: "#1C1917",
                  }}
                />
              </div>
              <div
                style={{
                  width: 28,
                  height: 36,
                  backgroundColor: "#1C1917",
                  borderRadius: 6,
                }}
              />
            </div>
          )}

          {pose === "thinking" && (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                zIndex: 2,
              }}
            >
              <div style={{ display: "flex", gap: 36 }}>
                {/* Slanted Eyes Looking Up-Right */}
                <div
                  style={{
                    width: 30,
                    height: 28,
                    backgroundColor: "#1C1917",
                    borderRadius: "14px 14px 4px 4px",
                    transform: "translateY(-4px) rotate(8deg)",
                  }}
                />
                <div
                  style={{
                    width: 30,
                    height: 28,
                    backgroundColor: "#1C1917",
                    borderRadius: "14px 14px 4px 4px",
                    transform: "translateY(-4px) rotate(8deg)",
                  }}
                />
              </div>
              {/* Question / Idea Telemetry line */}
              <span
                style={{
                  fontFamily: "monospace",
                  fontSize: 10,
                  fontWeight: 800,
                  color: "#1C1917",
                  marginTop: 6,
                }}
              >
                ? QUERYING KNOWLEDGE
              </span>
            </div>
          )}

          {pose === "pointing" && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 34,
                zIndex: 2,
              }}
            >
              {/* Eyes shifted right toward target card */}
              <div
                style={{
                  width: 30,
                  height: 36,
                  backgroundColor: "#1C1917",
                  borderRadius: 6,
                  transform: "translateX(6px)",
                }}
              />
              <div
                style={{
                  width: 30,
                  height: 36,
                  backgroundColor: "#1C1917",
                  borderRadius: 6,
                  transform: "translateX(6px)",
                }}
              />
            </div>
          )}

          {pose === "typing" && (
            <div
              style={{
                width: "90%",
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                padding: "0 6px",
                zIndex: 2,
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  width: "100%",
                  marginBottom: 4,
                }}
              >
                <div
                  style={{
                    width: 14,
                    height: 14,
                    backgroundColor: "#1C1917",
                    borderRadius: 3,
                  }}
                />
                <div
                  style={{
                    width: 14,
                    height: 14,
                    backgroundColor: "#1C1917",
                    borderRadius: 3,
                  }}
                />
              </div>
              <div
                style={{
                  fontFamily: "monospace",
                  fontSize: 9,
                  fontWeight: 900,
                  color: "#1C1917",
                  lineHeight: 1.3,
                }}
              >
                &gt; AGENT.EXECUTE()
                <br />
                &gt; DELEGATED: OK (0 ERR)
              </div>
            </div>
          )}

          {pose === "coffee" && (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                zIndex: 2,
              }}
            >
              {/* Happy curved LCD pixels (^ _ ^) */}
              <div style={{ display: "flex", gap: 32 }}>
                <div
                  style={{
                    width: 28,
                    height: 12,
                    borderTop: "6px solid #1C1917",
                    borderRadius: "14px 14px 0 0",
                  }}
                />
                <div
                  style={{
                    width: 28,
                    height: 12,
                    borderTop: "6px solid #1C1917",
                    borderRadius: "14px 14px 0 0",
                  }}
                />
              </div>
              <span
                style={{
                  fontFamily: "monospace",
                  fontSize: 9,
                  fontWeight: 800,
                  color: "#1C1917",
                  marginTop: 6,
                }}
              >
                STATUS: CHILLIN' // &#123;Z&#125;
              </span>
            </div>
          )}

          {/* Stamped Battery & Status Icons on Screen */}
          <div
            style={{
              position: "absolute",
              bottom: 6,
              left: 10,
              display: "flex",
              alignItems: "center",
              gap: 4,
            }}
          >
            <div
              style={{
                width: 12,
                height: 6,
                border: "1px solid #1C1917",
                borderRadius: 1,
                display: "flex",
                alignItems: "center",
                padding: 1,
              }}
            >
              <div
                style={{
                  width: "70%",
                  height: "100%",
                  backgroundColor: "#1C1917",
                }}
              />
            </div>
            <span
              style={{
                fontFamily: "monospace",
                fontSize: 8,
                fontWeight: 700,
                color: "#1C1917",
              }}
            >
              84%
            </span>
          </div>
          <span
            style={{
              position: "absolute",
              bottom: 6,
              right: 10,
              fontFamily: "monospace",
              fontSize: 8,
              fontWeight: 800,
              color: "#1C1917",
            }}
          >
            48KHz // REC
          </span>
        </div>

        {/* 4. BOTTOM HARDWARE KEYS */}
        <div
          style={{
            width: "100%",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginTop: 10,
            padding: "0 6px",
          }}
        >
          {/* Mini Playdate Style D-Pad */}
          <div
            style={{
              width: 28,
              height: 14,
              backgroundColor: "#292524",
              borderRadius: 4,
              border: "1.5px solid #1C1917",
            }}
          />
          {/* Tactile Round Keys */}
          <div style={{ display: "flex", gap: 6 }}>
            <div
              style={{
                width: 12,
                height: 12,
                borderRadius: "50%",
                backgroundColor: "#D6D3D1",
                border: "1.5px solid #1C1917",
              }}
            />
            <div
              style={{
                width: 12,
                height: 12,
                borderRadius: "50%",
                backgroundColor: "#D6D3D1",
                border: "1.5px solid #1C1917",
              }}
            />
            <div
              style={{
                width: 12,
                height: 12,
                borderRadius: "50%",
                backgroundColor: "#EA580C", // Braun orange shutter
                border: "1.5px solid #1C1917",
              }}
            />
          </div>
        </div>
      </div>

      {/* 5. POSE-AWARE FLOATING MAGNETIC HANDS */}
      {/* LEFT HAND */}
      <div
        style={{
          position: "absolute",
          left: pose === "typing" ? 40 : 10,
          bottom:
            pose === "coffee"
              ? 54
              : pose === "typing"
              ? 18 + typeTapL
              : 30,
          width: 36,
          height: 36,
          backgroundColor: "#FFFFFF",
          border: "3px solid #1C1917",
          borderRadius: "50%",
          boxShadow: "3px 3px 0px #1C1917",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 15,
        }}
      >
        <div
          style={{
            width: 10,
            height: 10,
            borderRadius: "50%",
            backgroundColor: "#2563EB",
          }}
        />
      </div>

      {/* RIGHT HAND */}
      <div
        style={{
          position: "absolute",
          right:
            pose === "pointing"
              ? -14
              : pose === "thinking"
              ? 16
              : pose === "typing"
              ? 40
              : 10,
          bottom:
            pose === "pointing"
              ? 58
              : pose === "thinking"
              ? 76
              : pose === "typing"
              ? 18 + typeTapR
              : 30,
          width: 36,
          height: 36,
          backgroundColor: "#FFFFFF",
          border: "3px solid #1C1917",
          borderRadius: "50%",
          boxShadow: "3px 3px 0px #1C1917",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          transform:
            pose === "pointing"
              ? "rotate(-40deg)"
              : pose === "thinking"
              ? "rotate(-25deg)"
              : "none",
          zIndex: 15,
        }}
      >
        {pose === "pointing" ? (
          /* Mechanical stylus pointer */
          <div
            style={{
              width: 6,
              height: 24,
              backgroundColor: "#EA580C",
              borderRadius: 3,
              border: "1px solid #1C1917",
              transform: "translateY(-8px)",
            }}
          />
        ) : (
          <div
            style={{
              width: 10,
              height: 10,
              borderRadius: "50%",
              backgroundColor: "#EA580C",
            }}
          />
        )}
      </div>

      {/* PROP: CERAMIC ESPRESSO MUG WITH {Z} LOGO (COFFEE POSE) */}
      {pose === "coffee" && (
        <div
          style={{
            position: "absolute",
            bottom: 24,
            left: 28,
            backgroundColor: "#FFFFFF",
            border: "2.5px solid #1C1917",
            borderRadius: "4px 4px 10px 10px",
            width: 30,
            height: 36,
            boxShadow: "3px 3px 0px #1C1917",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 16,
          }}
        >
          <span
            style={{
              fontSize: 10,
              fontFamily: "monospace",
              fontWeight: 900,
              color: "#EA580C",
            }}
          >
            &#123;Z&#125;
          </span>
          {/* Subtle pixel steam */}
          <div
            style={{
              position: "absolute",
              top: -10,
              width: 3,
              height: 6,
              backgroundColor: "rgba(28, 25, 23, 0.4)",
              borderRadius: 2,
              transform: `translateY(${Math.sin(frame / 12) * 2}px)`,
            }}
          />
        </div>
      )}

      {/* 6. BASE SHADOW */}
      <div
        style={{
          width: size * 0.52,
          height: 12,
          backgroundColor: "rgba(28, 25, 23, 0.2)",
          borderRadius: "50%",
          marginTop: 14,
        }}
      />
    </div>
  );
};
